import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useApp } from '../context/AppContext';
import { profileApi } from '../services/api';
import { isNativeIOS, restoreIAPPurchases, openSubscriptionManagement } from '../services/iap';
import toast from 'react-hot-toast';
import {
  ChefHat, ShieldCheck, FileText, RotateCcw, Crown, ExternalLink,
  Edit3, Check, LogOut, MoonStar, Sun, Monitor,
} from 'lucide-react';
import { Card, Button } from '../components/common';

// Section header pattern per shared-docs/dark-mode.md and profile-account-deletion.md:
// `text-sm font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide mb-3`.
const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide mb-3">
    {children}
  </h3>
);

const Row = ({ label, value, hint, action }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <div className="min-w-0 flex-1">
      <div className="text-sm text-gray-600 dark:text-night-muted">{label}</div>
      {value !== undefined && (
        <div className="text-sm font-medium text-gray-900 dark:text-night-text break-words">{value}</div>
      )}
      {hint && <div className="text-xs text-gray-500 dark:text-night-muted">{hint}</div>}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

function ProfileSection() {
  const { user, isAuthenticated, isConfigured } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAuthenticated && isConfigured) {
      profileApi.get({ silent: true })
        .then((data) => { if (data?.displayName) setDisplayName(data.displayName); })
        .catch(() => {});
    }
  }, [isAuthenticated, isConfigured]);

  async function handleSave() {
    if (!nameInput.trim()) return;
    setSaving(true);
    try {
      await profileApi.update({ displayName: nameInput.trim() });
      setDisplayName(nameInput.trim());
      setEditing(false);
      toast.success('Name updated');
    } catch {
      // Toast already shown by API client; nothing inline.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <SectionTitle>Profile</SectionTitle>
      <div className="space-y-3">
        <Row label="Email" value={isAuthenticated ? user?.email : 'Not signed in'} />

        {isAuthenticated ? (
          <div className="flex items-start justify-between gap-3 py-1">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-gray-600 dark:text-night-muted">Display Name</div>
              {editing ? (
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Enter your name"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-night-card rounded-lg text-sm bg-white dark:bg-night-card dark:text-night-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Button onClick={handleSave} disabled={saving} className="px-3 min-h-0 py-2">
                    <Check size={16} />
                  </Button>
                  <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving} className="px-3 min-h-0 py-2">
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="text-sm font-medium text-gray-900 dark:text-night-text">{displayName || '—'}</div>
              )}
            </div>
            {!editing && (
              <button
                onClick={() => { setNameInput(displayName); setEditing(true); }}
                aria-label="Edit display name"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-500 dark:text-night-muted"
              >
                <Edit3 size={16} />
              </button>
            )}
          </div>
        ) : (
          <Button variant="secondary" onClick={() => navigate('/auth/login')} className="w-full">
            Sign in to sync recipes
          </Button>
        )}
      </div>
    </Card>
  );
}

function PreferencesSection() {
  const { theme, setTheme } = useApp();
  const opts = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'system', label: 'System', icon: Monitor },
    { value: 'dark', label: 'Dark', icon: MoonStar },
  ];
  return (
    <Card>
      <SectionTitle>Preferences</SectionTitle>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-gray-600 dark:text-night-muted">Theme</div>
        <div role="radiogroup" aria-label="Theme" className="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-night-surface">
          {opts.map((opt) => {
            const selected = theme === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                role="radio"
                aria-checked={selected}
                onClick={() => setTheme(opt.value)}
                className={[
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  selected
                    ? 'bg-white dark:bg-night-card text-gray-900 dark:text-night-text shadow-sm'
                    : 'text-gray-600 dark:text-night-muted',
                ].join(' ')}
              >
                <Icon size={14} aria-hidden="true" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function SubscriptionSection() {
  const { isPremium, promptUpgrade, refreshSubscription } = useSubscription();
  const [restoring, setRestoring] = useState(false);

  async function handleRestore() {
    if (restoring) return;
    setRestoring(true);
    try {
      if (isNativeIOS()) {
        await restoreIAPPurchases();
        // CdvPurchase will fire the approved handler asynchronously; give it time.
        setTimeout(async () => {
          await refreshSubscription();
          setRestoring(false);
          toast.success('Purchases restored');
        }, 3000);
      } else {
        await refreshSubscription();
        toast(isPremium ? 'Subscription is active' : 'No active subscription found');
        setRestoring(false);
      }
    } catch {
      toast.error('Failed to restore purchases');
      setRestoring(false);
    }
  }

  return (
    <Card>
      <SectionTitle>Subscription</SectionTitle>
      <div className="space-y-3">
        <Row
          label="Current Plan"
          value={
            <span className={isPremium ? 'text-primary-600 dark:text-primary-400 font-semibold' : ''}>
              {isPremium ? 'Premium' : 'Free'}
            </span>
          }
        />
        {!isPremium ? (
          <Button onClick={() => promptUpgrade('default')} className="w-full">
            <Crown size={16} aria-hidden="true" />
            Upgrade to Premium
          </Button>
        ) : isNativeIOS() ? (
          <Button variant="secondary" onClick={openSubscriptionManagement} className="w-full">
            <ExternalLink size={16} aria-hidden="true" />
            Manage Subscription
          </Button>
        ) : (
          <p className="text-xs text-gray-500 dark:text-night-muted">
            Your subscription is managed through the App Store. Open the Cooksmith
            iOS app (or Settings → Apple ID → Subscriptions on your iPhone) to
            make changes.
          </p>
        )}
        <Button variant="secondary" onClick={handleRestore} disabled={restoring} className="w-full">
          <RotateCcw size={16} aria-hidden="true" className={restoring ? 'animate-spin' : ''} />
          {restoring ? 'Restoring…' : 'Restore Purchases'}
        </Button>
      </div>
    </Card>
  );
}

function LegalSection() {
  const navigate = useNavigate();
  return (
    <Card>
      <SectionTitle>Legal</SectionTitle>
      <div className="space-y-1">
        <button
          onClick={() => navigate('/privacy')}
          className="flex items-center justify-between w-full py-2 text-left hover:bg-gray-50 dark:hover:bg-night-card rounded-lg px-2 -mx-2"
        >
          <span className="text-sm text-gray-700 dark:text-night-text flex items-center gap-2">
            <ShieldCheck size={16} aria-hidden="true" />
            Privacy Policy
          </span>
          <ExternalLink size={14} className="text-gray-400 dark:text-night-muted" aria-hidden="true" />
        </button>
        <button
          onClick={() => navigate('/terms')}
          className="flex items-center justify-between w-full py-2 text-left hover:bg-gray-50 dark:hover:bg-night-card rounded-lg px-2 -mx-2"
        >
          <span className="text-sm text-gray-700 dark:text-night-text flex items-center gap-2">
            <FileText size={16} aria-hidden="true" />
            Terms of Use (EULA)
          </span>
          <ExternalLink size={14} className="text-gray-400 dark:text-night-muted" aria-hidden="true" />
        </button>
      </div>
    </Card>
  );
}

function DeleteAccountSection() {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  // Single "Account" header per shared-docs/profile-account-deletion.md — the card body
  // swaps between the red trigger button and the type-DELETE confirmation form.
  async function handleDelete() {
    if (confirmText !== 'DELETE') return;
    setError('');
    setDeleting(true);
    try {
      await deleteAccount();
      navigate('/auth/login', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete account');
      setDeleting(false);
    }
  }

  return (
    <Card>
      <SectionTitle>Account</SectionTitle>
      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full text-left"
        >
          <span className="text-sm font-medium text-red-600 dark:text-red-400">Delete Account</span>
        </button>
      ) : (
        <div className="space-y-3">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              This action is permanent and cannot be undone.
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              All your recipes, meal plans, grocery lists and preferences will be permanently
              deleted. Active subscriptions must be cancelled separately through the App Store
              or your payment provider.
            </p>
          </div>

          <label className="block">
            <span className="block text-xs text-gray-500 dark:text-night-muted mb-1">
              Type <strong>DELETE</strong> to confirm
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 dark:border-night-card rounded-lg text-sm bg-white dark:bg-night-card dark:text-night-text focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </label>

          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || deleting}
              className="flex-1"
            >
              {deleting ? 'Deleting…' : 'Permanently Delete Account'}
            </Button>
            <Button
              variant="secondary"
              disabled={deleting}
              onClick={() => { setShowConfirm(false); setConfirmText(''); setError(''); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function SettingsPage() {
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate('/');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ChefHat size={20} className="text-primary-600" aria-hidden="true" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-night-text">Settings</h2>
      </div>

      <ProfileSection />
      <PreferencesSection />
      {isAuthenticated && <SubscriptionSection />}
      <LegalSection />

      {isAuthenticated && (
        <>
          <DeleteAccountSection />
          <Button variant="danger" onClick={handleSignOut} className="w-full">
            <LogOut size={16} aria-hidden="true" />
            Sign Out
          </Button>
        </>
      )}
    </div>
  );
}
